import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useBrandByIdQuery,
  useBrandDetailsUpdateMutation,
  useBrandFileCreateMutation,
  useBrandFileDeleteMutation,
  useBrandTagsQuery,
  useCountryQuery,
  useIndustryQuery,
} from 'api';
import { FormInstance, FormProps, notification, Upload } from 'components';
import { MAX_FILE_SIZE } from 'constants/index';
import { ErrorMappingValueType } from 'typings';
import { BrandDetailsFormType } from 'typings/form/brand/details';
import { IBrandDetailsUpdate } from './index';

export const useBrandDetailsEdit = (props: { form: FormInstance } & IBrandDetailsUpdate) => {
  const { onCancel, form, open } = props;
  const { id } = useParams() as { id: string };
  const { data: brandDetails = {} } = useBrandByIdQuery({ id });
  const { data: tagData = [] } = useBrandTagsQuery();
  const { data: countryData = [] } = useCountryQuery();
  const { data: industryData = [] } = useIndustryQuery();
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);

  const [updateBrandDetails, { isLoading: detailsUpdateIsLoading }] = useBrandDetailsUpdateMutation();
  const [brandFileCreate, { isLoading: fileCreateIsLoading }] = useBrandFileCreateMutation();
  const [brandFileDelete, { isLoading: fileDeleteIsLoading }] = useBrandFileDeleteMutation();
  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const onCancelModal = useCallback(() => {
    form.resetFields();
    setIsSaveBtnDisabled(true);
    onCancel();
  }, [form, onCancel]);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        name: brandDetails.name,
        officialDealer: brandDetails.officialDealer,
        webSiteUrl: brandDetails.webSiteUrl,
        countryId: brandDetails.country?.id,
        industryId: brandDetails.industry?.id,
        manualTags: brandDetails.manualTags,
        manufacturerWebSiteUrl: brandDetails.manufacturerWebSiteUrl,
        additionalNotes: brandDetails.additionalNotes,
        logo: brandDetails.files?.LOGO
          ? [
              {
                uid: '-1',
                status: 'done',
                name: brandDetails.files.LOGO.split('fileName=')?.[1] || 'Brand Logo',
                imgUrl: brandDetails.files?.LOGO,
              },
            ]
          : [],
      });
    }
  }, [brandDetails, open, form]);

  const checkErrors = useCallback(async () => {
    let isDisabled = false;
    try {
      await form.validateFields();
      isDisabled = false;
    } catch (errorInfo: any) {
      isDisabled = !!errorInfo?.errorFields?.length;
    } finally {
      setIsSaveBtnDisabled(isDisabled);
    }
  }, [form]);

  const handleFileSizeValidation = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      form.setFieldValue('logo', []);
      form.setFields([
        {
          name: 'logo',
          errors: [`File exceeds the maximum size of 5 MB.`],
        },
      ]);
      setIsSaveBtnDisabled(true);
      return Upload.LIST_IGNORE;
    } else {
      form.setFields([
        {
          name: 'logo',
          errors: undefined,
        },
      ]);
      setIsSaveBtnDisabled(false);
    }
    return false;
  };

  const handleError = useCallback(
    (error: any) => {
      if (!error.data.success) {
        const { data = {} } = error.data;
        Object.keys(data).forEach((_key: string) => {
          openNotificationWithIcon({
            type: 'error',
            message: data[_key],
          });
        });
      } else if (error.data.error) {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const onFinish: FormProps<BrandDetailsFormType>['onFinish'] = useCallback(
    async (values: BrandDetailsFormType) => {
      try {
        const { logo } = form.getFieldsValue();
        const payload = { ...values, logo: undefined };
        const shouldDeleteLogo = brandDetails.files?.LOGO && !logo?.length;
        payload.id = id;
        const formData = new FormData();

        // If logo is selected and not a new logo
        if (logo?.length && id && logo[0]?.uid !== '-1') {
          const file = logo[0];
          formData.append('files', file.originFileObj, file.name);
          formData.append('brandId', id);
        }

        const { data, error }: any = await updateBrandDetails(payload);

        if (data?.success) {
          if (shouldDeleteLogo) {
            const [_, fileName] = brandDetails.files.LOGO.split('fileName=');
            const deleteResponse = await brandFileDelete({ name: fileName, id });
            if (deleteResponse.data?.success) {
              openNotificationWithIcon({
                type: 'success',
                message: 'The brand details were successfully updated.',
              });
              onCancelModal();
            } else {
              handleError(deleteResponse.error);
            }
          } else if (formData.has('files')) {
            const createResponse = await brandFileCreate(formData);
            if (createResponse.data?.success) {
              openNotificationWithIcon({
                type: 'success',
                message: 'The brand details were successfully updated.',
              });
              onCancelModal();
            } else {
              handleError(createResponse.error);
            }
          } else {
            openNotificationWithIcon({
              type: 'success',
              message: 'The brand details were successfully updated.',
            });
            onCancelModal();
          }
        } else {
          handleError(error);
        }
      } catch (err) {
        console.log(err, 'error');
      }
    },
    [
      updateBrandDetails,
      openNotificationWithIcon,
      onCancelModal,
      id,
      brandFileCreate,
      brandDetails,
      brandFileDelete,
      form,
      handleError,
    ]
  );

  return {
    onFinish,
    industryData,
    countryData,
    contextHolder,
    brandDetails,
    tagData,
    checkErrors,
    isSaveBtnDisabled,
    onCancelModal,
    handleFileSizeValidation,
    isLoading: detailsUpdateIsLoading || fileCreateIsLoading || fileDeleteIsLoading,
  };
};
